import React, { useState, useEffect, useCallback, useRef } from 'react';
import { hairdresserService } from '../../../_services/hairdresser.service';

/**
 * ClientPickerScreen - Search and select a client for POS checkout.
 * Matches the diary NewBookingPanel client picker UI.
 */
const ClientPickerScreen = ({ onSelect, onBack }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const timeoutRef = useRef(null);

  const fetchClients = useCallback((searchKey, pageNum = 1, append = false) => {
    if (pageNum === 1) setSearching(true);
    else setLoadingMore(true);

    hairdresserService.getClientsPaginated({ searchKey, page: pageNum, limit: 20, sortField: 'name', sortOrder: 'asc' })
      .then(response => {
        const data = response?.data?.responseData || response?.data?.data || {};
        const clients = data.clients || data.clientList || [];
        const arr = Array.isArray(clients) ? clients : [];
        if (append) {
          setResults(prev => [...prev, ...arr]);
        } else {
          setResults(arr);
        }
        setPage(pageNum);
        setHasMore(arr.length >= 20);
        setSearching(false);
        setLoadingMore(false);
      })
      .catch(() => {
        setSearching(false);
        setLoadingMore(false);
        setHasMore(false);
      });
  }, []);

  // Initial load
  useEffect(() => {
    fetchClients('');
  }, [fetchClients]);

  // Debounced search
  const handleSearch = (val) => {
    setQuery(val);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      fetchClients(val.trim());
    }, 300);
  };

  // Infinite scroll
  const handleScroll = (e) => {
    if (!hasMore || loadingMore) return;
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      fetchClients(query.trim(), page + 1, true);
    }
  };

  const WALKIN_ID = '64f5c7789a069cda0188fea3';

  return (
    <div className="co-screen-slide-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Select Client</h2>
      </div>

      <div className="bdp-picker-search">
        <svg className="bdp-picker-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          className="bdp-picker-search-input"
          placeholder="Search by name, email, or phone..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          autoFocus={window.innerWidth >= 768}
        />
      </div>

      <div className="bdp-picker-list bdp-picker-list--fullscreen" onScroll={handleScroll}>
        {/* Walk-in */}
        <button type="button" className="nbp-client-picker-item nbp-client-picker-item--walkin" onClick={() => onSelect({ _id: WALKIN_ID, name: 'Walk-in Client' })}>
          <div className="nbp-client-picker-avatar nbp-client-picker-avatar--walkin">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3C6.9 3 6 3.9 6 5C6 6.1 6.9 7 8 7C9.1 7 10 6.1 10 5C10 3.9 9.1 3 8 3Z" fill="currentColor"/>
              <path d="M8 8.5C5.79 8.5 4 9.79 4 11.5V13H12V11.5C12 9.79 10.21 8.5 8 8.5Z" fill="currentColor"/>
            </svg>
          </div>
          <div className="nbp-client-picker-info">
            <span className="nbp-client-picker-name">Walk-in Client</span>
            <span className="nbp-client-picker-sub">No client details required</span>
          </div>
        </button>

        {searching && <div className="bdp-picker-loading">Searching...</div>}

        {!searching && query.trim() && results.length === 0 && (
          <div className="bdp-picker-loading">No clients found</div>
        )}

        {results.map((c, i) => (
          <button
            key={c._id || i}
            type="button"
            className="nbp-client-picker-item"
            onClick={() => onSelect(c)}
          >
            <div className="nbp-client-picker-avatar">
              {(c.name || '?')[0]}
            </div>
            <div className="nbp-client-picker-info">
              <span className="nbp-client-picker-name">{c.name}</span>
              {c.email && !c.email.includes('@beuapp.com') && (
                <span className="nbp-client-picker-sub">{c.email}</span>
              )}
              {c.phoneNumber && (
                <span className="nbp-client-picker-sub">{c.phoneNumber}</span>
              )}
            </div>
          </button>
        ))}
        {loadingMore && <div className="bdp-picker-loading">Loading more...</div>}
      </div>
    </div>
  );
};

export default ClientPickerScreen;
